import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Box, Typography, Button, TextField, CircularProgress, Alert } from '@mui/material';
import { useNavigate } from 'react-router-dom';
import { authService } from '../../../services/authService';

const WelcomeScreen: React.FC = () => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    age: '',
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value,
    });
    if (error) setError('');
  };

  const handleNext = async () => {
    setLoading(true);
    setError('');
    try {
      const ageNumber = parseInt(formData.age);
      if (!formData.firstName.trim() || !formData.lastName.trim() || isNaN(ageNumber) || ageNumber < 8 || ageNumber > 120) {
        throw new Error(t('onb.welcome.invalidFields'));
      }

      const response = await authService.updateUserInitialInfo(
        formData.firstName,
        formData.lastName,
        ageNumber
      );

      if (response) {
        navigate('/onboarding/capital');
      } else {
        throw new Error(t('onb.welcome.saveError'));
      }
    } catch (err: any) {
      console.error('Erreur lors de la sauvegarde des infos d\'onboarding:', err);
      setError(err.message || t('onb.unexpected'));
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box
      sx={{
        minHeight: '100vh',
        width: '100vw',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        backgroundColor: '#000000',
        p: { xs: 3, md: 4 },
      }}
    >
      <Box
        sx={{
          width: '100%',
          maxWidth: '550px',
          p: { xs: 4, md: 8 },
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'center',
          backgroundColor: '#000000',
          borderRadius: '8px',
          boxShadow: '0 4px 20px rgba(0, 0, 0, 0.5)',
        }}
      >
        <Typography variant="h4" sx={{ mb: 2, color: '#fff', textAlign: 'center' }}>
          {t('onb.welcome.title')}
        </Typography>
        <Typography variant="body1" sx={{ mb: 4, color: 'rgba(255,255,255,0.7)', textAlign: 'center' }}>
          {t('onb.welcome.subtitle')}
        </Typography>

        {error && (
          <Alert severity="error" sx={{ mb: 2, backgroundColor: 'rgba(244, 67, 54, 0.1)', border: '1px solid rgba(244, 67, 54, 0.3)', color: '#ff6b6b' }}>
            {error}
          </Alert>
        )}

        <TextField
          fullWidth
          name="firstName"
          label={t('profile.firstName')}
          value={formData.firstName}
          onChange={handleChange}
          sx={{
            mb: 2,
            '& .MuiOutlinedInput-root': {
              backgroundColor: '#1a1a1a',
              borderRadius: '8px',
              height: '56px',
            },
            '& .MuiInputBase-input': {
              color: '#fff',
            },
            '& .MuiInputLabel-root': {
              color: 'rgba(255,255,255,0.7)',
            },
            '& .MuiOutlinedInput-notchedOutline': {
              borderColor: 'transparent',
            },
            '&:hover .MuiOutlinedInput-notchedOutline': {
              borderColor: '#f1c086',
            },
            '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
              borderColor: '#f1c086',
            },
          }}
          InputLabelProps={{
            shrink: true,
          }}
        />
        <TextField
          fullWidth
          name="lastName"
          label={t('profile.lastName')}
          value={formData.lastName}
          onChange={handleChange}
          sx={{
            mb: 2,
            '& .MuiOutlinedInput-root': {
              backgroundColor: '#1a1a1a',
              borderRadius: '8px',
              height: '56px',
            },
            '& .MuiInputBase-input': {
              color: '#fff',
            },
            '& .MuiInputLabel-root': {
              color: 'rgba(255,255,255,0.7)',
            },
            '& .MuiOutlinedInput-notchedOutline': {
              borderColor: 'transparent',
            },
            '&:hover .MuiOutlinedInput-notchedOutline': {
              borderColor: '#f1c086',
            },
            '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
              borderColor: '#f1c086',
            },
          }}
          InputLabelProps={{
            shrink: true,
          }}
        />
        <TextField
          fullWidth
          name="age"
          label={t('onb.welcome.age')}
          type="number"
          value={formData.age}
          onChange={handleChange}
          sx={{
            mb: 4,
            '& .MuiOutlinedInput-root': {
              backgroundColor: '#1a1a1a',
              borderRadius: '8px',
              height: '56px',
            },
            '& .MuiInputBase-input': {
              color: '#fff',
            },
            '& .MuiInputLabel-root': {
              color: 'rgba(255,255,255,0.7)',
            },
            '& .MuiOutlinedInput-notchedOutline': {
              borderColor: 'transparent',
            },
            '&:hover .MuiOutlinedInput-notchedOutline': {
              borderColor: '#f1c086',
            },
            '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
              borderColor: '#f1c086',
            },
          }}
          InputLabelProps={{
            shrink: true,
          }}
        />

        <Button
          fullWidth
          variant="contained"
          onClick={handleNext}
          disabled={loading}
          sx={{
            height: '56px',
            borderRadius: '8px',
            backgroundColor: '#f1c086',
            '&:hover': {
              backgroundColor: '#e0b076',
            },
            color: '#000',
            fontSize: '1rem',
            fontWeight: 'bold',
          }}
        >
          {loading ? <CircularProgress size={24} color="inherit" /> : t('common.continue')}
        </Button>
      </Box>
    </Box>
  );
};

export default WelcomeScreen; 